import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Award,
  ChevronRight,
  Lightbulb,
  Play,
  Eye,
  Globe,
  ChevronDown,
} from "lucide-react";
import { debounce } from "lodash";
import { useNavigate } from "react-router-dom";

import BaseAnchor from "@/components_v2/Anchor/BaseAnchor";
import BaseAccordion from "@/components_v2/Accordion/BaseAccordion";
import BaseButton from "@/components_v2/Button/BaseButton";
import BaseDropdownButton from "@/components_v2/Button/BaseDropdownButton";
import BaseCheckbox from "@/components_v2/FormFields/Checkbox/BaseCheckbox";
import InfoCard from "@/components_v2/Card/InfoCard";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import BaseDivider from "@/components_v2/Divider/BaseDivider";
import { BUTTON_VARIANTS } from "@/components_v2/Button/constants";
import {
  useGetSurveyChecklistQuery,
  useGetSurveySamplesQuery,
  useUpdateSurveyChecklistMutation,
} from "@/redux/service";
import { GuidancePageProps } from "@/features/survey/guidance/types";
import {
  ABOUT_THE_SIP_CARDS,
  AFTER_SUBMISSION_CARDS,
  CALENDAR_ITEMS,
  FAQ_ITEMS,
  GUIDANCE_PAGE_ANCHOR_ITEMS,
  NEW_FOR_2026_CARDS,
  PREPARATION_CHECKLIST_ACCORDIONS,
} from "./constants";
import CalendarCard from "./components/CalendarCard";
import ExemplarySipCard from "./components/ExemplarySipCard";
import { SipPreviews } from "@/shared/utils/constants";

import "./styles.css";

const GuidancePage = ({ survey }: GuidancePageProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: surveyOrganizationSamples } = useGetSurveySamplesQuery(
    survey.id,
  );
  const { data: checklistData } = useGetSurveyChecklistQuery(survey.details.id);
  const [updateChecklist] = useUpdateSurveyChecklistMutation();
  const [checklistChecked, setChecklistChecked] = useState<
    Record<string, boolean>
  >({});
  const [selectedLanguage, setSelectedLanguage] = useState(
    SipPreviews.EN.value,
  );

  const debouncedSave = useRef(
    debounce((state: Record<string, boolean>) => {
      updateChecklist({
        assignmentId: survey.details.id,
        data: state,
      });
    }, 800),
  ).current;

  useEffect(() => {
    if (checklistData) {
      setChecklistChecked(checklistData);
    }
  }, [checklistData]);

  useEffect(() => {
    return () => debouncedSave.cancel();
  }, [debouncedSave]);

  const handleCheckChange = useCallback(
    (id: string, checked: boolean) => {
      setChecklistChecked((prev) => {
        const next = { ...prev, [id]: checked };
        debouncedSave(next);
        return next;
      });
    },
    [debouncedSave],
  );

  const sipPreviewMenuProps = {
    items: SipPreviews.asList().map(
      (opt: { value: string; label: string }) => ({
        key: opt.value,
        label: opt.label,
        onClick: () => setSelectedLanguage(opt.value),
      }),
    ),
  };

  const handleDownloadPreview = () => {
    const link =
      (SipPreviews as unknown as Record<string, { link: string }>)[
        selectedLanguage
      ]?.link ?? SipPreviews.EN.link;
    window.open(link, "_blank", "noopener,noreferrer");
  };

  const checklistAccordionItems = PREPARATION_CHECKLIST_ACCORDIONS.map(
    ({ key: accordionKey, icon: Icon, titleKey, items, exemplarySips }) => ({
      key: accordionKey,
      label: (
        <div className="items-center justify-start gap-300">
          <span className="checklist-accordion-icon">
            <Icon size={20} />
          </span>
          <span className="checklist-accordion-title body-m-medium">
            {t(titleKey)}
          </span>
        </div>
      ),
      children: (
        <div className="flex flex-col gap-400">
          <div className="guidance-page-checklist-content-list flex flex-col gap-200 body-m-regular">
            {items.map(({ id, labelKey }) => (
              <BaseCheckbox
                key={id}
                label={t(labelKey)}
                checked={checklistChecked[id] ?? false}
                className="body-m-regular"
                onChange={(checked) => handleCheckChange(id, checked)}
              />
            ))}
          </div>

          {exemplarySips && (
            <>
              <BaseDivider />

              <div className="flex flex-col gap-300">
                <div className="flex items-center gap-200">
                  <Award size={20} />

                  <span className="checklist-accordion-title body-m-medium">
                    {t(exemplarySips.subsectionTitleKey)}
                  </span>
                </div>

                <p className="guidance-page-section-description guidance-page-exemplary-sips-intro body-m-regular">
                  {t(exemplarySips.introKey)}
                </p>

                {surveyOrganizationSamples && (
                  <div className="guidance-page-exemplary-cards">
                    {surveyOrganizationSamples.map((sample) => {
                      const reportPath = `/reports/${survey.sampleSource}/overview?organization=${sample.organization.id}`;
                      const reportViewPath = sample.reportViewUuid
                        ? `${window.location.origin}/public/reports/views/${sample.reportViewUuid}`
                        : "";
                      return (
                        <ExemplarySipCard
                          key={sample.organization.id}
                          title={sample.organization.name}
                          reportPath={reportPath}
                          reportViewPath={reportViewPath}
                          surveyType={survey.type}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      ),
    }),
  );

  const faqAccordionItems = FAQ_ITEMS.map(
    ({ key: itemKey, questionKey, answerKey }) => ({
      key: itemKey,
      label: (
        <span className="guidance-page-faq-question body-m-medium">
          {t(questionKey)}
        </span>
      ),
      children: (
        <p className="guidance-page-section-description body-m-regular">
          {t(answerKey)}
        </p>
      ),
    }),
  );

  const anchorItems = GUIDANCE_PAGE_ANCHOR_ITEMS.map(
    ({ key, href, titleKey, icon: Icon }) => ({
      key,
      href,
      title: (
        <span className="guidance-page-anchor-item body-m-medium flex items-center gap-200">
          <BaseSvgRenderer component={Icon} width="20" height="20" />
          {t(titleKey)}
        </span>
      ),
    }),
  );

  return (
    <div className="guidance-page flex full-width justify-self-center gap-500">
      <aside
        className="guidance-page-panel flex-shrink-0"
        aria-label={t("survey.labels.guidancePage")}
        data-samples-count={surveyOrganizationSamples?.length}
      >
        <BaseAnchor items={anchorItems} offsetTop={100} />
      </aside>

      <main className="guidance-page-content flex flex-col flex-1 gap-600">
        <section id="about" className="flex flex-col gap-300">
          <h2 className="header-l-medium">
            {t("survey.guidancePage.preparingTitle")}
          </h2>

          <p className="guidance-page-section-description body-m-regular">
            {t("survey.guidancePage.preparingDescription")}
          </p>

          <div className="flex flex-wrap gap-200 items-center">
            <BaseButton
              variant={BUTTON_VARIANTS.PRIMARY}
              trailingIcon={
                <BaseSvgRenderer
                  component={ChevronRight}
                  width="20"
                  height="20"
                />
              }
              onClick={() => navigate(`/reports/${survey.id}`)}
            >
              {t("survey.guidancePage.startYourSurvey")}
            </BaseButton>

            <BaseDropdownButton
              className="guidance-page-download-preview-button"
              menu={sipPreviewMenuProps}
              onClick={handleDownloadPreview}
              icon={
                <div className="flex items-center gap-200">
                  <Globe size={16} />
                  <ChevronDown size={20} />
                </div>
              }
            >
              {t("downloadSurvey")} ({SipPreviews.getLabel(selectedLanguage)})
            </BaseDropdownButton>

            <BaseButton
              variant={BUTTON_VARIANTS.TERTIARY}
              leadingIcon={
                <BaseSvgRenderer component={Play} width="20" height="20" />
              }
              onClick={() =>
                window.open(
                  "https://prme.storylane.io/demo/tuxjeaqartam?embed=popup",
                  "_blank",
                )
              }
              target="_blank"
            >
              {t("survey.guidancePage.viewSurveyTutorial")}
            </BaseButton>
          </div>
        </section>

        <BaseDivider />

        <section id="about" className="flex flex-col gap-300">
          <h2 className="header-m-medium">
            {t("survey.guidancePage.aboutTheSurvey")}
          </h2>

          <p className="guidance-page-section-description body-m-regular">
            {t("survey.guidancePage.aboutTheSurveyDescription")}
          </p>

          <div className="guidance-page-about-cards gap-300">
            {ABOUT_THE_SIP_CARDS.map(
              ({ key, icon: Icon, titleKey, descriptionKey }) => (
                <InfoCard
                  key={key}
                  icon={<Icon size={24} />}
                  title={t(titleKey)}
                  description={t(descriptionKey)}
                />
              ),
            )}
          </div>
        </section>

        <BaseDivider />

        <section id="new-for-2026" className="flex flex-col gap-300">
          <h2 className="header-m-medium">
            {t("survey.guidancePage.newFor2026")}
          </h2>

          <p className="guidance-page-section-description body-m-regular">
            {t("survey.guidancePage.newFor2026Description")}
          </p>

          <div className="guidance-page-new-cards">
            {NEW_FOR_2026_CARDS.map(
              ({ key, icon: Icon, titleKey, descriptionKey }) => (
                <InfoCard
                  key={key}
                  icon={<Icon size={24} />}
                  title={t(titleKey)}
                  description={t(descriptionKey)}
                />
              ),
            )}
          </div>
        </section>

        <BaseDivider />

        <section id="checklist" className="flex flex-col gap-300">
          <h2 className="header-m-medium">
            {t("survey.guidancePage.preparationChecklistTitle")}
          </h2>

          <p className="guidance-page-section-description body-m-regular">
            {t("survey.guidancePage.preparationChecklistSubtitle")}
          </p>

          <BaseAccordion
            className="guidance-page-checklist"
            expandIconPosition="end"
            items={checklistAccordionItems}
          />
        </section>

        <BaseDivider />

        <section id="how-to-complete" className="flex flex-col gap-300">
          <h2 className="header-m-medium">
            {t("survey.guidancePage.howToCompleteTheSurvey")}
          </h2>

          <p className="guidance-page-section-description body-m-regular">
            {t("survey.guidancePage.howToCompleteDescription")}
          </p>

          <div className="guidance-page-tips-card flex flex-col gap-300">
            <div className="flex items-center gap-200">
              <Lightbulb size={20} />

              <h3 className="guidance-page-tips-card-title body-m-medium">
                {t("survey.guidancePage.howToCompleteTipsTitle")}
              </h3>
            </div>

            <div className="guidance-page-tips-card-columns">
              <ul className="guidance-page-tips-card-columns-list body-m-regular">
                {[
                  "howToCompleteTip1",
                  "howToCompleteTip2",
                  "howToCompleteTip3",
                ].map((key) => (
                  <li
                    className="guidance-page-tips-card-columns-list-item"
                    key={key}
                  >
                    {t(`survey.guidancePage.${key}`)}
                  </li>
                ))}
              </ul>

              <ul className="guidance-page-tips-card-columns-list body-m-regular">
                {[
                  "howToCompleteTip4",
                  "howToCompleteTip5",
                  "howToCompleteTip6",
                ].map((key) => (
                  <li
                    className="guidance-page-tips-card-columns-list-item"
                    key={key}
                  >
                    {t(`survey.guidancePage.${key}`)}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="guidance-page-tutorial-card flex flex-col items-center gap-300">
            <span className="guidance-page-tutorial-card-icon flex items-center justify-center">
              <Eye size={24} />
            </span>

            <h3 className="guidance-page-tutorial-card-title header-m-medium text-center">
              {t("survey.guidancePage.howToCompleteTutorialTitle")}
            </h3>

            <p className="guidance-page-section-description body-m-regular text-center">
              {t("survey.guidancePage.howToCompleteTutorialDescription")}
            </p>

            <BaseButton
              variant={BUTTON_VARIANTS.PRIMARY}
              trailingIcon={
                <BaseSvgRenderer
                  component={ChevronRight}
                  width="20"
                  height="20"
                />
              }
            >
              {t("survey.guidancePage.howToCompleteTutorialButton")}
            </BaseButton>
          </div>
        </section>

        <BaseDivider />

        <section id="after-submission" className="flex flex-col gap-300">
          <h2 className="header-m-medium">
            {t("survey.guidancePage.afterTheSubmission")}
          </h2>

          <p className="guidance-page-section-description body-m-regular">
            {t("survey.guidancePage.afterSubmissionDescription")}
          </p>

          <div className="guidance-page-after-wrapper">
            <div className="guidance-page-after-cards gap-300">
              {AFTER_SUBMISSION_CARDS.map(
                ({ key: cardKey, icon: Icon, titleKey, descriptionKey }) => (
                  <InfoCard
                    key={cardKey}
                    icon={<Icon size={24} />}
                    title={t(titleKey)}
                    description={t(descriptionKey)}
                    iconBackgroundColor="var(--green-100)"
                    iconColor="var(--green-700)"
                  />
                ),
              )}
            </div>
          </div>
        </section>

        <BaseDivider />

        <section id="faq" className="flex flex-col gap-300">
          <h2 className="header-m-medium">
            {t("survey.guidancePage.faqTitle")}
          </h2>

          <p className="guidance-page-section-description body-m-regular">
            {t("survey.guidancePage.faqSubtitle")}
          </p>

          <BaseAccordion
            className="guidance-page-faq"
            expandIconPosition="end"
            items={faqAccordionItems}
          />
        </section>

        <BaseDivider />

        <section id="calendar" className="flex flex-col gap-300">
          <h2 className="header-l-medium">
            {t("survey.guidancePage.calendar")}
          </h2>

          <p className="guidance-page-section-description body-m-regular">
            {t("survey.guidancePage.calendarSubtitle")}
          </p>

          <div className="flex flex-col gap-300">
            {CALENDAR_ITEMS.map(
              ({ key: itemKey, dateKey, titleKey, descriptionKey, tagKey }) => (
                <CalendarCard
                  key={itemKey}
                  date={t(dateKey)}
                  title={t(titleKey)}
                  description={t(descriptionKey)}
                  tag={tagKey != null ? t(tagKey) : undefined}
                />
              ),
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default GuidancePage;
